/**
 * Keeps the app shell aligned to the real drawable viewport.
 *
 * iOS Home Screen apps occasionally resolve `100dvh` to the content viewport
 * instead of the full screen. The result is a correctly rendered app inside a
 * shell that is one safe-area band too short: the header is displaced and the
 * bottom dock floats above a black strip. A measured pixel height avoids that
 * WebKit disagreement while still allowing Stage Manager and normal Safari
 * windows to keep their actual window dimensions.
 */

export type AppViewportMeasurement = {
  innerHeight: number;
  innerWidth: number;
  documentHeight: number;
  screenHeight: number;
  screenWidth: number;
  visualHeight: number;
  visualOffsetTop: number;
  standalone: boolean;
};

const finitePositive = (value: number) => Number.isFinite(value) && value > 0;

export function resolveAppViewportHeight(measurement: AppViewportMeasurement): number {
  const {
    innerHeight,
    innerWidth,
    documentHeight,
    screenHeight,
    screenWidth,
    visualHeight,
    visualOffsetTop,
    standalone,
  } = measurement;

  const candidates = [innerHeight, documentHeight, visualHeight + visualOffsetTop]
    .filter(finitePositive);
  const measuredWindow = candidates.length > 0 ? Math.max(...candidates) : 0;

  // A full-screen iPhone/iPad Home Screen app owns the whole CSS screen.
  // Stage Manager and split-screen windows do not, so screen.height is only
  // trusted when the app width is effectively the physical screen width.
  const isFullScreenStandalone = standalone
    && finitePositive(screenWidth)
    && finitePositive(innerWidth)
    && Math.abs(screenWidth - innerWidth) <= 2;
  const plausibleSafeAreaShortfall = screenHeight >= measuredWindow
    && screenHeight - measuredWindow <= 180;

  if (isFullScreenStandalone && finitePositive(screenHeight) && plausibleSafeAreaShortfall) {
    return Math.round(screenHeight);
  }

  return Math.round(measuredWindow || (finitePositive(screenHeight) ? screenHeight : 0));
}

export function resolveWebKeyboardInset(
  shellHeight: number,
  visualHeight: number,
  visualOffsetTop: number,
  nativePlatform: boolean,
): number {
  if (nativePlatform || !finitePositive(shellHeight) || !finitePositive(visualHeight)) return 0;
  const occluded = Math.round(shellHeight - visualHeight - Math.max(0, visualOffsetTop));
  // Closed iOS PWAs can retain a 40-70px status/home-indicator delta. That is
  // safe-area chrome, not a keyboard, and is already handled by env() padding.
  return occluded > 80 && occluded < shellHeight * 0.75 ? occluded : 0;
}

export function initAppViewport() {
  if (typeof window === 'undefined' || typeof document === 'undefined') return;

  const root = document.documentElement;
  const standalone = () => Boolean(
    (window.navigator as Navigator & { standalone?: boolean }).standalone
      || window.matchMedia?.('(display-mode: standalone)').matches,
  );
  const nativePlatform = () => Boolean(root.dataset.nativePlatform);

  const sync = () => {
    const visualViewport = window.visualViewport;
    const shellHeight = resolveAppViewportHeight({
      innerHeight: window.innerHeight,
      innerWidth: window.innerWidth,
      documentHeight: root.clientHeight,
      screenHeight: window.screen?.height ?? 0,
      screenWidth: window.screen?.width ?? 0,
      visualHeight: visualViewport?.height ?? window.innerHeight,
      visualOffsetTop: visualViewport?.offsetTop ?? 0,
      standalone: standalone(),
    });

    if (shellHeight > 0) root.style.setProperty('--app-shell-height', `${shellHeight}px`);
    const webKeyboardInset = resolveWebKeyboardInset(
      shellHeight,
      visualViewport?.height ?? window.innerHeight,
      visualViewport?.offsetTop ?? 0,
      nativePlatform(),
    );
    root.style.setProperty('--web-kb', `${webKeyboardInset}px`);
    root.classList.toggle('web-kb-open', webKeyboardInset > 0);

  };

  sync();
  window.addEventListener('resize', sync, { passive: true });
  window.addEventListener('orientationchange', sync, { passive: true });
  window.addEventListener('pageshow', sync, { passive: true });
  window.visualViewport?.addEventListener('resize', sync, { passive: true });
  window.visualViewport?.addEventListener('scroll', sync, { passive: true });
  document.addEventListener('visibilitychange', () => {
    if (!document.hidden) window.requestAnimationFrame(sync);
  });
}
